import React from 'react';
import LegalDocumentLayout from './LegalDocumentLayout';

const VendorInviteeTerms = () => (
  <LegalDocumentLayout title="Vendor &amp; Invitee Terms" subtitle="Version 1.0 · Effective upon acceptance · Governed by the laws of the State of Delaware, USA">
    <p>
      These Vendor and Invitee Terms ("Terms") govern access to the Axaris platform by individuals and
      organisations ("Vendor" or "You") who have been invited by an Axaris Customer to participate in a
      third-party risk management ("TPRM") assessment, vendor evaluation, or related process. These Terms
      constitute a binding agreement between You and Cloud Guidant Inc. ("Cloud Guidant"). By accepting these
      Terms, clicking "Accept," or accessing the platform via an invitation link, You agree to be fully bound by
      them.
    </p>
    <p>
      You are accessing Axaris solely because You have been invited by an Axaris Customer (the "Inviting
      Organisation"). Your access is limited in scope, time-bound, and subject to the controls of the Inviting
      Organisation. Cloud Guidant is not a party to any commercial relationship between You and the Inviting
      Organisation.
    </p>

    <h2>1. Scope and Nature of Access</h2>
    <p>Your access to Axaris is strictly limited to:</p>
    <ul>
      <li>Completing questionnaires, submitting documentation, and responding to assessment requests issued by the Inviting Organisation</li>
      <li>Viewing assessment-related content expressly made available to You by the Inviting Organisation</li>
    </ul>
    <p>
      You have no right to access any other part of the Axaris platform, any other Tenant's environment, or any
      data beyond what is directly provided to You in the context of the specific assessment for which You were
      invited.
    </p>
    <p>
      Your access is temporary and will expire upon completion of the assessment, revocation by the Inviting
      Organisation, or termination by Cloud Guidant, whichever occurs first. Cloud Guidant may revoke Your access
      at any time without notice or liability.
    </p>

    <h2>2. Data You Submit</h2>
    <p>
      By submitting any information, documentation, questionnaire responses, or materials ("Vendor Submission")
      through the platform, You:
    </p>
    <ul>
      <li>Warrant that You are authorised to submit such information on behalf of Your organisation and that it is accurate, complete, and lawful to share</li>
      <li>Warrant that Vendor Submissions do not infringe any third-party intellectual property rights, violate any confidentiality obligation, or breach any applicable law</li>
      <li>Grant Cloud Guidant a worldwide, perpetual, royalty-free licence to process, store, and use Vendor Submissions to provide the TPRM assessment services to the Inviting Organisation, to generate AI-powered risk scores and outputs, and to create aggregated, anonymised, or de-identified derivatives</li>
      <li>Acknowledge that Vendor Submissions will be processed by Cloud Guidant's AI systems and that the Inviting Organisation will receive AI-generated risk assessments, scores, and findings based on Your submissions</li>
    </ul>
    <p>
      You are solely responsible for the accuracy, completeness, and lawfulness of all Vendor Submissions. Cloud
      Guidant bears no liability for any assessment outcome arising from inaccurate or incomplete submissions.
    </p>

    <h2>3. Assessment Outputs and AI Disclaimer</h2>
    <p className="legal-caps">
      ALL RISK SCORES, FINDINGS, ASSESSMENTS, RECOMMENDATIONS, AND OTHER OUTPUTS GENERATED BY THE AXARIS PLATFORM
      IN CONNECTION WITH YOUR ASSESSMENT ARE AI-GENERATED, PROBABILISTIC, AND ADVISORY IN NATURE. THEY DO NOT
      CONSTITUTE A PROFESSIONAL OPINION, AUDIT, CERTIFICATION, OR GUARANTEE OF ANY KIND REGARDING YOUR
      ORGANISATION'S SECURITY POSTURE, COMPLIANCE STATUS, OR RISK PROFILE. CLOUD GUIDANT MAKES NO WARRANTY AS TO
      THE ACCURACY OR COMPLETENESS OF ANY ASSESSMENT OUTPUT.
    </p>
    <p>
      Assessment outputs are provided to the Inviting Organisation and not to You unless the Inviting Organisation
      chooses to share them. Cloud Guidant has no obligation to provide You with a copy of any assessment output.
      Cloud Guidant is not responsible for any decision made by the Inviting Organisation based on assessment
      outputs.
    </p>

    <h2>4. Intellectual Property</h2>
    <p>
      The Axaris platform, including all AI models, scoring logic, assessment frameworks, questionnaire structures,
      and methodology, is the exclusive property of Cloud Guidant. Nothing in these Terms grants You any right,
      title, or interest in any Cloud Guidant Intellectual Property. You must not attempt to reverse engineer,
      probe, reconstruct, copy, or replicate any aspect of the platform or its outputs.
    </p>
    <p>
      Aggregated, anonymised, or de-identified data derived from Vendor Submissions and from assessments across
      all vendors is the exclusive property of Cloud Guidant and may be used for any commercial purpose without
      restriction or compensation.
    </p>

    <h2>5. Confidentiality</h2>
    <p>You agree to keep confidential:</p>
    <ul>
      <li>The fact and content of any assessment conducted via the platform</li>
      <li>All information provided to You by the Inviting Organisation in connection with the assessment</li>
      <li>All aspects of the Axaris platform You observe during Your access, including its structure, features, and methodology</li>
    </ul>
    <p>
      You must not disclose any of the foregoing to any third party without the prior written consent of both the
      Inviting Organisation and Cloud Guidant.
    </p>
    <p>
      You acknowledge that Cloud Guidant's platform structure, AI logic, assessment methodology, and scoring
      frameworks constitute trade secrets and Confidential Information of Cloud Guidant, and that any unauthorised
      disclosure would cause irreparable harm for which monetary damages would be insufficient.
    </p>

    <h2>6. Acceptable Use</h2>
    <p>
      You must not: attempt to access any area of the platform beyond Your authorised scope; attempt to access or
      probe any other organisation's data or environment; upload malicious code or harmful content; attempt to
      reverse engineer or reconstruct the platform's AI logic or methodology; misrepresent Your identity or
      authority; submit false or misleading information; or use the platform for any purpose other than completing
      the assessment for which You were invited. Cloud Guidant may terminate Your access immediately and without
      notice for any violation.
    </p>

    <h2>7. Data Processing and Privacy</h2>
    <p>
      Cloud Guidant processes personal data submitted in Vendor Submissions as a Data Processor on behalf of the
      Inviting Organisation (the Data Controller) and, where applicable, as a Data Controller in its own right for
      platform operation purposes. Personal data is processed in accordance with Cloud Guidant's Privacy Policy,
      available at cloudguidant.com. Data submitted is stored within the Inviting Organisation's designated data
      residency region. You are responsible for ensuring that the submission of any personal data in Vendor
      Submissions complies with applicable privacy law, including obtaining any required consents.
    </p>

    <h2>8. No Warranties and Limitation of Liability</h2>
    <p className="legal-caps">
      THE PLATFORM IS PROVIDED "AS IS." CLOUD GUIDANT DISCLAIMS ALL WARRANTIES IN CONNECTION WITH YOUR ACCESS,
      INCLUDING AS TO PLATFORM AVAILABILITY, ASSESSMENT ACCURACY, AND FITNESS FOR ANY PURPOSE.
    </p>
    <p>
      Cloud Guidant's total aggregate liability to You under these Terms shall not exceed USD $100, to the maximum
      extent permitted by applicable law. Cloud Guidant shall not be liable for any indirect, incidental,
      consequential, or punitive damages arising from Your access or any assessment outcome.
    </p>

    <h2>9. Indemnification</h2>
    <p>
      You shall defend, indemnify, and hold harmless Cloud Guidant and its Indemnified Parties from all claims
      arising from: Your breach of these Terms; Vendor Submissions including any claim that they infringe
      third-party rights; Your misuse of the platform; or any misrepresentation made by You in the assessment
      process. This obligation survives termination of Your access.
    </p>

    <h2>10. Termination</h2>
    <p>
      Your access terminates automatically upon completion of the assessment, expiry of the invitation, or
      revocation by the Inviting Organisation or Cloud Guidant. Upon termination, all obligations of
      confidentiality and intellectual property protection continue in full force. Cloud Guidant will retain Vendor
      Submissions in accordance with its standard data retention schedule and the Inviting Organisation's data
      retention settings.
    </p>

    <h2>11. Governing Law and Dispute Resolution</h2>
    <p>
      These Terms are governed by the laws of Delaware, USA. All disputes are resolved by binding individual
      arbitration under AAA Commercial Rules in Wilmington, Delaware.
    </p>
    <p className="legal-caps">
      YOU WAIVE ALL RIGHTS TO CLASS, COLLECTIVE, OR REPRESENTATIVE PROCEEDINGS AND TO JURY TRIAL. CLAIMS NOT
      SUBMITTED WITHIN ONE (1) YEAR OF THE TRIGGERING EVENT ARE PERMANENTLY BARRED.
    </p>

    <h2>12. General</h2>
    <p>
      These Terms constitute the entire agreement between You and Cloud Guidant regarding Your access to Axaris as
      an invitee. Cloud Guidant may update these Terms at any time; continued access constitutes acceptance. These
      Terms do not create any employment, agency, or partnership relationship. Cloud Guidant's failure to enforce
      any provision is not a waiver.
    </p>
  </LegalDocumentLayout>
);

export default VendorInviteeTerms;
